import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle, useCallback, useMemo } from 'react';
import gsap from 'gsap';
import './PhoneGalleryAnimation.css';

// 5개의 갤러리 이미지와 결과 이미지 import
const galleryImages = [
  { src: '/resource/favorite_1.jpg', result: '/resource/favorite_1_result.jpg' },
  { src: '/resource/favorite_2.jpg', result: '/resource/favorite_2_result.jpg' },
  { src: '/resource/favorite_3.jpg', result: '/resource/favorite_3_result.jpg' },
  { src: '/resource/favorite_4.jpg', result: '/resource/favorite_4_result.jpg' },
  { src: '/resource/favorite_5.jpg', result: '/resource/favorite_5_result.jpg' },
];

// Placeholder 이미지들
const placeholderImages = [
  '/resource/placeholder_1.jpg',
  '/resource/placeholder_2.jpg',
  '/resource/placeholder_3.jpg',
  '/resource/placeholder_4.jpg',
  '/resource/placeholder_5.jpg',
  '/resource/placeholder_6.jpg',
  '/resource/placeholder_7.jpg',
];

// 랜덤 placeholder 이미지 인덱스 배열 생성 함수
const generateRandomPlaceholderIndices = (length) => {
  return Array.from({ length }, () => Math.floor(Math.random() * placeholderImages.length));
};

const PhoneGalleryAnimation = forwardRef((props, ref) => {
  const containerRef = useRef(null);
  const phoneScreenRef = useRef(null);
  const galleryRef = useRef(null);
  const selectedItemRefs = useRef([]); // 여러 갤러리 아이템을 참조
  const fingerCursorRef = useRef(null);
  const analysisScreenRef = useRef(null);
  const resultScreenRef = useRef(null);
  const progressBarRef = useRef(null);
  const progressTextRef = useRef(null);
  const [progressValue, setProgressValue] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const timelineRef = useRef(null);
  const loopTimeoutRef = useRef(null);
  
  // 갤러리 placeholder용 랜덤 이미지 인덱스 (한 번만 생성)
  const randomPlaceholderIndices = useMemo(() => generateRandomPlaceholderIndices(18), []);

  // 부모 컴포넌트에서 애니메이션을 제어할 수 있도록 함수 노출
  useImperativeHandle(ref, () => ({
    playAnimation: () => {
      playAnimationSequence();
    },
    resetAnimation: () => {
      resetAnimation();
    },
    stopLoop: () => {
      stopLoop();
    }
  }));

  const stopLoop = () => {
    if (loopTimeoutRef.current) {
      clearTimeout(loopTimeoutRef.current);
      loopTimeoutRef.current = null;
    }
    if (timelineRef.current) {
      timelineRef.current.kill();
    }
  };

  const resetAnimation = () => {
    stopLoop();
    setProgressValue(0);
    setCurrentImageIndex(0);
    
    gsap.set(galleryRef.current, { opacity: 1, display: 'flex' });
    selectedItemRefs.current.forEach(item => {
      if (item) {
        gsap.set(item, { scale: 1 });
        item.classList.remove('selected');
      }
    });
    gsap.set(fingerCursorRef.current, { opacity: 0, scale: 1 });
    gsap.set(analysisScreenRef.current, { opacity: 0, display: 'none' });
    gsap.set(resultScreenRef.current, { opacity: 0, display: 'none' });
  };

  const playAnimationSequence = useCallback(() => {
    // 현재 선택된 아이템
    const selectedItemRef = selectedItemRefs.current[currentImageIndex];
    
    if (!selectedItemRef) {
      console.warn('Selected item ref not found for index:', currentImageIndex);
      return;
    }

    // 이전 타임라인이 있으면 정리
    if (timelineRef.current) {
      timelineRef.current.kill();
    }
    
    // 이전 타임아웃이 있으면 정리
    if (loopTimeoutRef.current) {
      clearTimeout(loopTimeoutRef.current);
    }

    // 모든 갤러리 아이템의 스타일 초기화 (이전 선택 효과 제거)
    selectedItemRefs.current.forEach(item => {
      if (item) {
        gsap.set(item, { 
          scale: 1,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)'
        });
        item.classList.remove('selected');
      }
    });

    const tl = gsap.timeline({
      onComplete: () => {
        // 4초간 결과 화면 유지 후 다음 이미지로 전환
        loopTimeoutRef.current = setTimeout(() => {
          // 갤러리 화면으로 돌아가기
          gsap.to(resultScreenRef.current, {
            opacity: 0,
            duration: 0.3,
            onComplete: () => {
              gsap.set(resultScreenRef.current, { display: 'none' });
              gsap.set(galleryRef.current, { opacity: 0, display: 'flex' });
              gsap.to(galleryRef.current, {
                opacity: 1,
                duration: 0.3,
                onComplete: () => {
                  // 갤러리가 완전히 표시된 후 다음 이미지 인덱스로 변경
                  setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
                }
              });
            }
          });
        }, 4000); // 결과 화면 4초 표시
      }
    });
    
    timelineRef.current = tl;

    // Phase 1: 갤러리 표시 (0~0.5초)
    tl.to({}, { duration: 0.5 });

    // 손가락 커서 등장 및 이동 애니메이션 (0.5~1.2초)
    const itemRect = selectedItemRef.getBoundingClientRect();
    const screenRect = phoneScreenRef.current?.getBoundingClientRect();
    
    if (itemRect && screenRect) {
      // phone-screen 내부 기준 좌표
      const targetX = itemRect.left + itemRect.width / 2 - screenRect.left;
      const targetY = itemRect.top + itemRect.height / 2 - screenRect.top;
      
      // 손가락 시작 위치 (화면 우측 하단에서 시작)
      const startX = screenRect.width * 0.7;
      const startY = screenRect.height * 0.8;
      
      gsap.set(fingerCursorRef.current, {
        x: startX,
        y: startY,
        opacity: 0,
        scale: 1
      });
      
      // 손가락 페이드인
      tl.to(fingerCursorRef.current, {
        opacity: 1,
        duration: 0.2
      }, 0.5);
      
      // 커서가 이미지 중앙으로 이동 (원형 커서는 60px이므로 -30으로 중앙 정렬)
      tl.to(fingerCursorRef.current, {
        x: targetX - 30,
        y: targetY - 30,
        duration: 0.5,
        ease: 'power2.inOut'
      }, 0.7);
    }

    // Phase 2: 이미지 탭 애니메이션 (1.2~1.5초)
    // 손가락 눌림 효과
    tl.to(fingerCursorRef.current, {
      scale: 0.85,
      duration: 0.1,
      ease: 'power2.in'
    }, 1.2);
    
    // 이미지 선택 효과 (주황색 그라디언트)
    tl.to(selectedItemRef, {
      scale: 0.95,
      duration: 0.15,
      ease: 'power2.out',
      onStart: () => {
        selectedItemRef.classList.add('selected');
      }
    }, 1.2);

    // 손가락 원래대로
    tl.to(fingerCursorRef.current, {
      scale: 1,
      duration: 0.1,
      ease: 'power2.out'
    }, 1.3);
    
    // 이미지도 원래대로
    tl.to(selectedItemRef, {
      scale: 1,
      duration: 0.1,
      ease: 'power2.out'
    }, 1.3);

    // 손가락 페이드아웃
    tl.to(fingerCursorRef.current, {
      opacity: 0,
      duration: 0.2
    }, 1.4);

    // Phase 3: AI 분석 화면 전환 (1.6~3.65초)
    tl.to(galleryRef.current, {
      opacity: 0,
      duration: 0.3,
      onComplete: () => {
        gsap.set(galleryRef.current, { display: 'none' });
      }
    }, 1.6);

    tl.set(analysisScreenRef.current, { display: 'flex', opacity: 0 }, 1.9);
    tl.to(analysisScreenRef.current, {
      opacity: 1,
      duration: 0.3
    }, 1.9);

    // 진행률 애니메이션 (1.9~3.65초, 1.75초 동안 - 30% 단축)
    tl.to({}, {
      duration: 1.75,
      onUpdate: function() {
        const progress = Math.round(this.progress() * 100);
        setProgressValue(progress);
      }
    }, 1.9);

    // Phase 4: 결과 표시 (3.65초~)
    tl.to(analysisScreenRef.current, {
      opacity: 0,
      duration: 0.3,
      onComplete: () => {
        gsap.set(analysisScreenRef.current, { display: 'none' });
        setProgressValue(0); // 진행률 초기화
      }
    }, 3.65);

    tl.set(resultScreenRef.current, { display: 'flex', opacity: 0 }, 3.95);
    tl.to(resultScreenRef.current, {
      opacity: 1,
      duration: 0.5,
      ease: 'power2.out'
    }, 3.95);
  }, [currentImageIndex]);

  // currentImageIndex가 변경될 때마다 애니메이션 재생
  useEffect(() => {
    // 갤러리가 표시된 후 애니메이션 시작
    const timer = setTimeout(() => {
      playAnimationSequence();
    }, 800);

    return () => {
      clearTimeout(timer);
    };
  }, [currentImageIndex, playAnimationSequence]);

  useEffect(() => {
    return () => {
      if (timelineRef.current) {
        timelineRef.current.kill();
      }
      if (loopTimeoutRef.current) {
        clearTimeout(loopTimeoutRef.current);
      }
    };
  }, []);

  // 갤러리 아이템 생성 (3열 6행 = 18개)
  // favorite 이미지들이 들어갈 위치 (0-indexed): 1, 4, 7, 10, 13
  const favoritePositions = [1, 4, 7, 10, 13];
  
  const galleryItems = Array.from({ length: 18 }, (_, index) => {
    // 현재 위치가 favorite 이미지 위치인지 확인
    const favoriteIndex = favoritePositions.indexOf(index);
    const isFavoriteItem = favoriteIndex !== -1;
    
    // favorite 위치는 favorite 이미지, 나머지는 placeholder 이미지
    let imageSrc;
    if (isFavoriteItem) {
      imageSrc = galleryImages[favoriteIndex].src;
    } else {
      imageSrc = placeholderImages[randomPlaceholderIndices[index]];
    }
    
    return (
      <div 
        key={index}
        ref={isFavoriteItem ? (el) => (selectedItemRefs.current[favoriteIndex] = el) : null}
        className={`gallery-item ${isFavoriteItem ? 'favorite-item' : ''}`}
      >
        <img 
          src={imageSrc} 
          alt={isFavoriteItem ? `Favorite ${favoriteIndex + 1}` : `Gallery ${index}`} 
          className="gallery-image" 
        />
      </div>
    );
  });

  return (
    <div className="phone-animation-container" ref={containerRef}>
      <div className="phone-frame">
        <div className="phone-notch" />
        <div className="phone-screen" ref={phoneScreenRef}>
          {/* 갤러리 그리드 */}
          <div className="gallery-grid" ref={galleryRef}>
            <div className="gallery-header">
              <div className="gallery-header-left">
                <svg className="gallery-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="7" height="7"/>
                  <rect x="14" y="3" width="7" height="7"/>
                  <rect x="14" y="14" width="7" height="7"/>
                  <rect x="3" y="14" width="7" height="7"/>
                </svg>
                <span className="gallery-title">갤러리</span>
              </div>
              <div className="gallery-header-right">
                <span className="gallery-count">124</span>
              </div>
            </div>
            <div className="gallery-items-container">
              {galleryItems}
            </div>
          </div>

          {/* 원형 커서 */}
          <div className="finger-cursor" ref={fingerCursorRef}>
            <div className="cursor-circle"></div>
            {/* 탭 효과 원 */}
            <div className="tap-ripple"></div>
          </div>

          {/* AI 분석 화면 */}
          <div className="analysis-screen" ref={analysisScreenRef}>
            <div className="analysis-content">
              <h3 className="analysis-title">AI 분석중</h3>
              <div className="dot-loading">
                <span className="dot"></span>
                <span className="dot"></span>
                <span className="dot"></span>
              </div>
              <div className="progress-container">
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    ref={progressBarRef}
                    style={{ width: `${progressValue}%` }}
                  />
                </div>
                <div className="progress-text" ref={progressTextRef}>
                  {progressValue}%
                </div>
              </div>
            </div>
          </div>

          {/* 결과 화면 */}
          <div className="result-screen" ref={resultScreenRef}>
            <img 
              src={galleryImages[currentImageIndex].result} 
              alt={`AI Analysis Result ${currentImageIndex + 1}`} 
              className="result-image" 
            />
          </div>
        </div>
      </div>
    </div>
  );
});

PhoneGalleryAnimation.displayName = 'PhoneGalleryAnimation';

export default PhoneGalleryAnimation;
